import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components";
import { ColumnDef, ColumnFiltersState, FlexRender, Row, useTable } from "@tanstack/react-table";
import type { DataTableFeatures } from "./data-table-features";
import { dataTableFeatures } from "./data-table-features";
import DataTablePagination from "./data-table-pagination";
import { DataTableToolbar, DataTableToolbarOptions, Filters } from "./data-table-toolbar";

type DataTableProps<TData extends Record<string, unknown>> = {
    toolbarOptions: DataTableToolbarOptions<TData>;
    columns: ColumnDef<DataTableFeatures, TData>[];
    data: TData[];
    onDoubleClick?: (row: Row<DataTableFeatures, TData>) => void;
};

function createInitialColumnFilterState<TData extends Record<string, unknown>>(filters: Filters<TData>): ColumnFiltersState {
    const { columnTextFilters, multiSelectFilters, booleanFilters } = filters;

    const filtersWithDefaultValues = [...(columnTextFilters || []), ...(multiSelectFilters || []), ...(booleanFilters || [])].filter(
        (filter) => filter.defaultValue
    );

    return filtersWithDefaultValues.map((filter) => ({
        id: filter.column,
        value: filter.defaultValue,
    }));
}

export function DataTable<TData extends Record<string, unknown>>({ columns, data, toolbarOptions, onDoubleClick }: DataTableProps<TData>) {
    const table = useTable({
        data,
        columns,
        features: dataTableFeatures,
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
            columnVisibility: toolbarOptions.columnVisibility?.defaultValues ?? {},
            columnFilters: toolbarOptions.filters ? createInitialColumnFilterState(toolbarOptions.filters) : [],
            globalFilter: "",
        },
        getColumnCanGlobalFilter: (column) => toolbarOptions.filters?.globalFilter?.columns.some((field) => field === column.id) ?? false,
        globalFilterFn: (row, columnId, filterValue) => {
            const searchValue = (filterValue as string).toLowerCase();
            const value = row.getValue(columnId);
            if (typeof value === "string" || typeof value === "number") {
                return String(value).toLowerCase().includes(searchValue);
            }
            if (Array.isArray(value) && value.every((el) => typeof el === "string")) {
                return value.some((el) => el.toLowerCase().includes(searchValue));
            }
            return false;
        },
    });

    return (
        <div>
            <DataTableToolbar table={table} toolbarOptions={toolbarOptions} />
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder ? null : <FlexRender header={header} />}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} onDoubleClick={() => onDoubleClick?.(row)}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            <FlexRender cell={cell} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={table.getVisibleFlatColumns().length} className="h-24 text-center">
                                    Geen resultaten.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <DataTablePagination table={table} />
        </div>
    );
}
